import { getCsrfToken } from '../../../javascripts/helpers/csrf';
import initImage from '../../image/javascripts';

const TYPE_BUCKETS = ['productions', 'articles', 'pages', 'mediathek'];

const escapeHtml = (str) =>
  String(str == null ? '' : str).replace(
    /[&<>"']/g,
    (c) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[c]
  );

class Search {
  constructor(element) {
    this.element = element;
    this.config = JSON.parse(element.dataset.config || '{}');
    this.labels = this.config.labels || {};
    this.minChars = this.config.minChars || 3;

    this.results = element.querySelector('.search__results');
    this.input = element.querySelector('.search__input');
    this.form = element.querySelector('.search__form');
    this.submitButton = element.querySelector('.search__submit');
    this.controlsEl = element.querySelector('[data-search-controls]');
    this.filtersEl = element.querySelector('[data-search-filters]');
    this.sortEl = element.querySelector('[data-search-sort]');
    this.moreEl = element.querySelector('[data-search-more]');
    this.suggestionsEl = element.querySelector('[data-search-suggestions]');

    // search state
    this.activeType = 'all';
    this.sort = 'relevance';
    this.offset = 0;
    this.pageSize = 50;
    this.lastResult = null;
    this.searchReqId = 0;

    // autocomplete state
    this.suggestions = [];
    this.activeSuggestion = -1;
    this.suggestReqId = 0;
    this.suggestTimer = null;

    // cached CSRF token (valid for the session)
    this.token = null;

    this.isValidSearch = false;

    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.input.addEventListener('blur', () =>
      window.setTimeout(() => this.hideSuggestions(), 120)
    );
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Sort toggle: Relevanz (default) ↔ Datum (postDate)
    if (this.sortEl) {
      this.sortEl.querySelectorAll('[data-sort-mode]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.getAttribute('data-sort-mode') || 'relevance';
          if (mode === this.sort) {
            return;
          }
          this.setSort(mode);
          this.runSearch();
        });
      });
    }

    // Load-more: append the next page of results
    if (this.moreEl) {
      this.moreEl.addEventListener('click', () => {
        this.offset += this.pageSize;
        this.runSearch({ append: true });
      });
    }
  }

  // ── Sort toggle ─────────────────────────────────────────────────────────────
  setSort(mode) {
    this.sort = mode === 'newest' ? 'newest' : 'relevance';

    if (!this.sortEl) {
      return;
    }

    this.sortEl.querySelectorAll('[data-sort-mode]').forEach((btn) => {
      const active = btn.getAttribute('data-sort-mode') === this.sort;
      btn.classList.toggle('search__sort-option--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  // ── CSRF ──────────────────────────────────────────────────────────────────
  async getToken(force = false) {
    if (this.token && !force) {
      return this.token;
    }

    const { token } = await getCsrfToken(this.config.csrfApiUrl);
    this.token = token;

    return token;
  }

  // ── Input handling ──────────────────────────────────────────────────────────
  handleInput() {
    const query = this.input.value.trim();
    this.isValidSearch = query.length >= this.minChars;
    this.submitButton.disabled = !this.isValidSearch;

    // Hide the topic tags as soon as the search is active (Zustand 1 → 2);
    // bring them back when the input is cleared/below the minimum length.
    this.element.classList.toggle('search--is-active', query.length >= this.minChars);

    window.clearTimeout(this.suggestTimer);

    if (query.length >= this.minChars) {
      // Instant feedback the moment the topic tags hide: a loading placeholder
      // shows right away, independent of whether the debounced fetch below
      // has resolved (or ever finds suggestions) yet.
      this.showSuggestionsLoading();
      this.suggestTimer = window.setTimeout(() => this.fetchSuggestions(query), 250);
    } else {
      this.hideSuggestions();
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    const query = this.input.value.trim();
    if (query.length < this.minChars) {
      return;
    }

    // A submit is a brand new query: reset type filter + sort, track it once.
    this.activeType = 'all';
    this.setSort('relevance');

    if (typeof window._paq !== 'undefined') {
      window._paq.push(['trackSiteSearch', query, false, false]);
    }

    this.hideSuggestions();
    this.runSearch();
  }

  // ── Full search ───────────────────────────────────────────────────────────
  async runSearch({ append = false } = {}) {
    const query = this.input.value.trim();
    if (query.length < this.minChars) {
      return;
    }

    // A fresh (non-append) search always starts from the top.
    if (!append) {
      this.offset = 0;
    }

    const reqId = ++this.searchReqId;

    if (append) {
      if (this.moreEl) {
        this.moreEl.disabled = true;
      }
    } else {
      this.element.classList.add('search--is-loading');
      this.submitButton.disabled = true;
    }

    try {
      const data = await this.postSearch(query, this.offset);

      // Drop stale responses (a newer request has been fired meanwhile).
      if (reqId !== this.searchReqId) {
        return;
      }

      if (append) {
        this.appendResults(data.html || '');
      } else {
        this.lastResult = data;
        this.results.innerHTML = data.html || '';
        this.renderFilters();
      }

      this.updateMoreButton(data.hasMore);
      initImage();
      this.dispatchModalUpdate();
    } catch (err) {
      // swallow – keep previous results visible; roll back the offset on a failed page
      if (append) {
        this.offset = Math.max(0, this.offset - this.pageSize);
      }
    } finally {
      if (reqId === this.searchReqId) {
        if (append) {
          if (this.moreEl) {
            this.moreEl.disabled = false;
          }
        } else {
          this.element.classList.remove('search--is-loading');
          this.submitButton.disabled = false;
        }
      }
    }
  }

  appendResults(html) {
    const grid = this.results.querySelector('.teaser-row__teasers');
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const newGrid = tmp.querySelector('.teaser-row__teasers');

    if (grid && newGrid) {
      while (newGrid.firstElementChild) {
        grid.appendChild(newGrid.firstElementChild);
      }
    } else if (html) {
      this.results.insertAdjacentHTML('beforeend', html);
    }
  }

  updateMoreButton(hasMore) {
    if (!this.moreEl) {
      return;
    }
    this.moreEl.hidden = !hasMore;
  }

  async postSearch(query, offset = 0, retry = true) {
    const token = await this.getToken();
    const params = new URLSearchParams({
      q: query,
      type: this.activeType,
      sort: this.sort,
      offset
    });

    const response = await fetch(`${this.config.apiUrl}?${params.toString()}`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token
      }
    });

    // Token may have expired – refresh once and retry.
    if (response.status === 400 && retry) {
      await this.getToken(true);
      return this.postSearch(query, offset, false);
    }

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status}`);
    }

    return response.json();
  }

  // ── Type filter tabs ─────────────────────────────────────────────────────
  renderFilters() {
    if (!this.filtersEl) {
      return;
    }

    const result = this.lastResult;
    if (!result || !result.total) {
      this.filtersEl.innerHTML = '';
      if (this.controlsEl) {
        this.controlsEl.hidden = true;
      }
      return;
    }

    const counts = result.counts || {};
    const tabs = [['all', this.labels.all || 'Alle', result.total]];
    TYPE_BUCKETS.forEach((bucket) => {
      const count = counts[bucket] || 0;
      if (count > 0) {
        tabs.push([bucket, this.labels[bucket] || bucket, count]);
      }
    });

    this.filtersEl.innerHTML = tabs
      .map(([key, label, count]) => {
        const active = key === this.activeType;
        return `<button type="button" role="tab" class="search__filter${
          active ? ' search__filter--active' : ''
        }" data-search-filter="${key}" aria-selected="${active ? 'true' : 'false'}">${escapeHtml(
          label
        )} (${count})</button>`;
      })
      .join('');

    if (this.controlsEl) {
      this.controlsEl.hidden = false;
    }

    this.filtersEl.querySelectorAll('[data-search-filter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const type = btn.getAttribute('data-search-filter') || 'all';
        if (type === this.activeType) {
          return;
        }
        this.activeType = type;
        // Re-fetch only the rendered set; counts stay the full set so no tracking.
        this.runSearch();
      });
    });
  }

  // ── Autocomplete ──────────────────────────────────────────────────────────
  async fetchSuggestions(query) {
    const reqId = ++this.suggestReqId;

    try {
      const params = new URLSearchParams({ q: query });
      const response = await fetch(`${this.config.suggestUrl}?${params.toString()}`, {
        method: 'GET'
      });

      if (!response.ok) {
        if (reqId === this.suggestReqId) {
          this.hideSuggestions();
        }
        return;
      }

      const data = await response.json();

      if (reqId !== this.suggestReqId) {
        return;
      }

      this.renderSuggestions(data.suggestions || []);
    } catch (err) {
      // ignore – autocomplete is non-critical, but don't leave the loading
      // placeholder stuck on screen forever
      if (reqId === this.suggestReqId) {
        this.hideSuggestions();
      }
    }
  }

  // Immediate response indicator: a staircase of bars shown the instant the
  // search becomes active, before we know if there'll be any suggestions.
  showSuggestionsLoading() {
    if (!this.suggestionsEl) {
      return;
    }

    this.suggestions = [];
    this.activeSuggestion = -1;

    this.suggestionsEl.innerHTML = `<li class="search__suggestion search__suggestion--loading" aria-hidden="true">
      <span class="search__suggestion-stairs">
        <span></span><span></span><span></span><span></span>
      </span>
    </li>`;

    this.suggestionsEl.hidden = false;
    this.suggestionsEl.setAttribute('aria-busy', 'true');
    this.input.setAttribute('aria-expanded', 'true');
  }

  renderSuggestions(list) {
    if (!this.suggestionsEl) {
      return;
    }

    this.suggestionsEl.removeAttribute('aria-busy');

    if (!list.length) {
      this.hideSuggestions();
      return;
    }

    this.suggestions = list;
    this.activeSuggestion = -1;

    this.suggestionsEl.innerHTML = list
      .map(
        (s, i) =>
          `<li class="search__suggestion" role="option" id="search-suggestion-${i}" data-index="${i}" aria-selected="false">
            <span class="search__suggestion-label">${escapeHtml(s.label)}</span>
            ${s.type ? `<span class="search__suggestion-type">${escapeHtml(s.type)}</span>` : ''}
          </li>`
      )
      .join('');

    this.suggestionsEl.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');

    this.suggestionsEl.querySelectorAll('.search__suggestion').forEach((el) => {
      // mousedown fires before the input's blur, so the choice is registered.
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.chooseSuggestion(parseInt(el.dataset.index, 10));
      });
    });
  }

  setActiveSuggestion(index) {
    this.activeSuggestion = index;

    this.suggestionsEl.querySelectorAll('.search__suggestion').forEach((el, idx) => {
      const active = idx === index;
      el.classList.toggle('search__suggestion--active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (index >= 0) {
      this.input.setAttribute('aria-activedescendant', `search-suggestion-${index}`);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  chooseSuggestion(index) {
    const suggestion = this.suggestions[index];
    if (!suggestion) {
      return;
    }

    this.input.value = suggestion.label;
    this.isValidSearch = suggestion.label.trim().length >= this.minChars;
    this.element.classList.add('search--is-active');
    this.hideSuggestions();

    // Treat a chosen suggestion like a fresh search.
    this.activeType = 'all';
    this.setSort('relevance');
    if (typeof window._paq !== 'undefined') {
      window._paq.push(['trackSiteSearch', suggestion.label, false, false]);
    }
    this.runSearch();
  }

  hideSuggestions() {
    if (!this.suggestionsEl) {
      return;
    }

    this.suggestionsEl.hidden = true;
    this.suggestionsEl.innerHTML = '';
    this.suggestionsEl.removeAttribute('aria-busy');
    this.suggestions = [];
    this.activeSuggestion = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  handleKeydown(e) {
    if (!this.suggestionsEl || this.suggestionsEl.hidden || !this.suggestions.length) {
      return;
    }

    const max = this.suggestions.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setActiveSuggestion(Math.min(this.activeSuggestion + 1, max));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setActiveSuggestion(Math.max(this.activeSuggestion - 1, -1));
        break;
      case 'Enter':
        if (this.activeSuggestion >= 0) {
          e.preventDefault();
          this.chooseSuggestion(this.activeSuggestion);
        }
        break;
      case 'Escape':
        this.hideSuggestions();
        break;
      default:
        break;
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  dispatchModalUpdate() {
    const ev = new CustomEvent(`modal-update-${this.config.modalId}`, {
      bubbles: true
    });
    window.dispatchEvent(ev);
  }
}

export default Search;
